import React, { useEffect, useState } from 'react';
import "../Styles/CreateUser.css";
import * as yup from "yup";
import { useNavigate, useLocation } from 'react-router-dom';
import { ErrorMessage, Formik, Form, Field } from "formik";
import Axios from "axios";
import { toast, ToastContainer, toast as toastHelper } from 'react-toastify';
import UserProfileHeader from "./UserProfileHeader";
import Img from "../Assets/icons/VELOXLogo.png";

const CreateUser = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const ROLE_LABEL_TO_NAME = {
    "First Approver": "First Approver",
    "Second Approver": "Second Approver",
    "Key Management": "YubiKey Manager",          // label vs backend name
    "Logistics Administrator": "Logistics Administrator",
    "Technician": "Technician",
    "Admin": "Admin",
  };

  const CONSTRAINED = new Set([
    "First Approver",
    "Second Approver",
    "YubiKey Manager",
    "Logistics Administrator",
  ]);

  const [takenRoles, setTakenRoles] = useState(new Set());

  useEffect(() => {
    // Use existing endpoint that returns velox_users (role is numeric)
    Axios.post('/get_users')
      .then(res => {
        const rows = res?.data?.data || res?.data || [];
        // Any row with role id in {2,3,4,5} counts as taken
        const taken = new Set();
        rows.forEach(r => {
          switch (Number(r.role)) {
            case 2: taken.add("First Approver"); break;
            case 3: taken.add("Second Approver"); break;
            case 4: taken.add("YubiKey Manager"); break;
            case 5: taken.add("Logistics Administrator"); break;
            default: break;
          }
        });
        setTakenRoles(taken);
      })
      .catch(() => {
        // Fail open (no disables) if we cannot load
        setTakenRoles(new Set());
      });
  }, []);

  const handleBack = () => {
    console.log("Back button clicked!");         
    console.log("Current path:", location.pathname);

    // Dismiss any active toasts before navigating
    toastHelper.dismiss();

    // Check if we can go back in history
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);  // Go back one step in history
      console.log("Navigating back one step.");
    } else {
      // If no history, navigate to the dashboard without adding an entry
      navigate('/dashboard', { replace: true });
      console.log("No history to go back, redirecting to dashboard.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('@user');
    localStorage.removeItem('userEmail');
    toastHelper.dismiss();  // Ensure any active toasts are dismissed before logout
    navigate('/', { replace: true });  // Use replace to avoid adding history
  };

  const handleCreateUser = (values, { resetForm }) => {
    Axios.post(`/create_user`, {
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
      yubikey: values.yubikeyserialnumber,
      adminEmail: localStorage.getItem('userEmail'),
      role: ROLE_LABEL_TO_NAME[values.role] || 'Technician',
    }).then((response) => {
      if (response && response.data && response.data.success) {
        toast.success(response.data.msg || "User created successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        resetForm();
        // // Optionally navigate to the user management page or dashboard after user creation
        // navigate('/user-management', { replace: true });  // Use replace to avoid extra entry
      }
      else {
        toast.error(response.data.msg || "Failed to add user, please try again!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
    }).catch((error) => {
      toast.error("Failed to add user, please try again!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    });
  };

  const validationsCreateUser = yup.object().shape({
    firstname: yup
      .string()
      .required("First name is mandatory"),
    lastname: yup
      .string()
      .required("Last name is mandatory"),
    email: yup
      .string()
      .email("Invalid email")
      .required("Email is mandatory"),
  });

  return (
    <div className="dashboard">
      <UserProfileHeader isAdmin={true} />
      <div className="all-users-card">
        <h1 className="Title-alluser">Create VELOX User</h1>
        <Formik
          initialValues={{
            firstname: '',
            lastname: '',
            email: '',
            password: '',
            yubikeyserialnumber: '',
            role: 'Technician',
          }}
          onSubmit={handleCreateUser}
          validationSchema={validationsCreateUser}
        >
          <Form className="register-form">
            <div className="register-form-group">
              <Field name="firstname" type='text' className="register-form-firstname-field" placeholder="First name" />
              <ErrorMessage component="span" name="firstname" className="adduser-form-error" />
            </div>

            <div className="register-form-group">
              <Field name="lastname" type='text' className="register-form-lastname-field" placeholder="Last name" />
              <ErrorMessage component="span" name="lastname" className="adduser-form-error" />
            </div>

            <div className="register-form-group">
              <Field name="email" type='text' className="register-form-email-field" placeholder="Email" />
              <ErrorMessage component="span" name="email" className="adduser-form-error" />
            </div>

            <div className="register-form-group">
              <Field as="select" name="role" className="register-form-lastname-field">
                {[
                  "First Approver",
                  "Second Approver",
                  "Key Management",           // shows as requested; maps to YubiKey Manager
                  "Logistics Administrator",
                  "Technician",
                  "Admin",
                ].map(label => {
                  const backendName = ROLE_LABEL_TO_NAME[label];
                  const isConstrained = CONSTRAINED.has(backendName);
                  const disabled = isConstrained && takenRoles.has(backendName);
                  return (
                    <option key={label} value={label} disabled={disabled}>
                      {label}{disabled ? " (unavailable)" : ""}
                    </option>
                  );
                })}
              </Field>
            </div>

            <div className="register-form-group">
              <Field name="yubikeyserialnumber" type='text' className="register-form-yubikeyserialnumber-field" placeholder="YubiKey Serial Number" />
            </div>

            <button className="createuser-button" type="submit">
              Create A VELOX User
            </button>
          </Form>
        </Formik>
      </div>
    </div>
  );
};

export default CreateUser;
